import AudiencesController from '@mm/state/controllers/AudiencesController';
import { Variable } from '@mm/state/interfaces/audiences';
import {
  FilterItem,
  FilterItemOperator,
  VariableType,
} from '@mm/state/model/AudiencesModel';
import { VariableOptionsModel } from '@mm/state/model/VariableOptionsModel';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import './AudienceFilterItemInput.pcss';
import { AudienceFilterItemInputOperator } from './AudienceFilterItemInputOperator';
import { AudienceFilterItemInputValue } from './AudienceFilterItemInputValue';
import { AudienceFilterItemInputVariable } from './AudienceFilterItemInputVariable';

interface OuterProps {
  filter: FilterItem;
  setFilter: (filter: FilterItem) => void;
  className?: string;
  hideOperatorAndValueUntilVariableSelected?: boolean;
  showBlankVariableOption?: boolean;
  setInputRef?: (ref: HTMLInputElement) => void;
  withEmoji?: boolean;
  css?: {
    section?: string;
    fieldTitle?: string;
    select?: string;
    selectActiveItem?: string;
    suggestionsContainer?: string;
    valueInput?: string;
    valueButton?: string;
    valueListing?: string;
    valueTagItem?: string;
  };
  chipTagItems?: boolean;
}
type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;
type Props = OuterProps & InnerProps;

export const MULTI_VALUES_OPERATORS: FilterItemOperator[] = [
  'equal',
  'not_equal',
];

export interface AudienceFilterItemInputChildProps {
  variables: Variable[];

  filter: FilterItem;
  setFilter: (filter: FilterItem) => void;
  variableOptions: VariableOptionsModel | undefined;
  allowMultipleValues: boolean;
  busy: boolean;
  className?: string;
  showBlankVariableOption?: boolean;
  setInputRef?: (ref: HTMLInputElement) => void;
  fetchingVariableOptions: boolean;
  withEmoji?: boolean;
  css?: OuterProps['css'];
  chipTagItems?: boolean;
}

const AudienceFilterItemInputComponent = (props: Props) => {
  const {
    variables,
    getVariableOptions,
    chatbotId,
    filter,
    setFilter,
    className,
    hideOperatorAndValueUntilVariableSelected,
    showBlankVariableOption,
    setInputRef,
    withEmoji,
    css,
    chipTagItems,
  } = props;

  const localSetFilter = useCallback(
    (filter: FilterItem) => {
      setFilter({
        ...filter,
        valid: !!(filter.operator && filter.value.length),
      });
    },
    [setFilter],
  );

  const [variableOptions, setVariableOptions] = useState<VariableOptionsModel>({
    variableDataKind: 'string',
    variableOperators: [],
    variableOptions: [],
  });
  const [fetchingVariableOptions, setFetchingVariableOptions] = useState(false);

  useEffect(() => {
    if (!filter.variableId) return;
    if (!fetchingVariableOptions) {
      const updateVariableOptions = async () => {
        setFetchingVariableOptions(true);
        try {
          const options = await getVariableOptions(
            chatbotId,
            filter.variableId,
            filter.variableType,
            filter.variable,
          );
          let filterUpdates = {};
          if (!filter.operator) {
            const operator = options.variableOperators[0].value;
            filterUpdates = {
              ...filterUpdates,
              operator,
              value: MULTI_VALUES_OPERATORS.includes(operator) ? [] : '',
            };
          }
          if (
            options.variableDataKind === 'date_time' &&
            !moment(filter.value, 'MM/DD/YYYY').isValid()
          ) {
            filterUpdates = {
              ...filterUpdates,
              value: moment().format('MM/DD/YYYY'),
            };
          }
          if (Object.keys(filterUpdates).length) {
            localSetFilter({ ...filter, ...filterUpdates });
          }
          setVariableOptions(options);
        } finally {
          setFetchingVariableOptions(false);
        }
      };
      updateVariableOptions();
    }
  }, [filter, filter.variableId, filter.variableType]);

  const allowMultipleValues = !!(
    variableOptions && MULTI_VALUES_OPERATORS.find(o => o === filter.operator)
  );

  const childProps: AudienceFilterItemInputChildProps = {
    filter,
    setFilter: localSetFilter,
    variables,
    variableOptions,
    allowMultipleValues,
    busy: fetchingVariableOptions,
    className,
    showBlankVariableOption,
    setInputRef,
    fetchingVariableOptions,
    withEmoji,
    css,
    chipTagItems,
  };

  return (
    <div className={classNames('audience-filter-item-input', className)}>
      <AudienceFilterItemInputVariable {...childProps} />
      {(!hideOperatorAndValueUntilVariableSelected || !!filter.variableId) && (
        <AudienceFilterItemInputOperator {...childProps} />
      )}
      {(!hideOperatorAndValueUntilVariableSelected || !!filter.variableId) && (
        <AudienceFilterItemInputValue {...childProps} />
      )}
    </div>
  );
};

const mapStateToProps = (state: StoreState) => ({
  chatbotId: state.view.chatbotEditor.activeChatbotId,
  variables: state.audiences.variables,
});
const mapDispatchToProps = () => ({
  getVariableOptions: (
    chatbotId: number,
    variableId: number,
    variableType: VariableType,
    variableName: string,
  ) =>
    AudiencesController.getVariableOptions(
      chatbotId,
      variableId,
      variableType,
      variableName,
    ),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

export const AudienceFilterItemInput = enhance(
  AudienceFilterItemInputComponent,
);
